import { OpenApiDocument, OpenApiOperation } from '../../api/documentation.api';

export interface ApiOperationModel extends OpenApiOperation {
    method: string;
    path: string;
    shortPath: string;
}

export interface ApiOperationGroupModel {
    tag: string;
    operations: ApiOperationModel[];
}

export function getApiOperationGroups(doc: OpenApiDocument): ApiOperationGroupModel[] {
    // Transform { [path]: { [method]: ... } } into { tag, operation: { method, path, ... } }[]:
    const taggedOperations = Object.entries(doc.paths)
        .map(([path, methods]) =>
            Object.entries(methods).map(([method, operation]) =>
                operation.tags
                    .filter(tag => tag !== 'CustomerOnly')
                    .map(tag => ({
                        tag,
                        operation: {
                            method: method.toUpperCase(),
                            path,
                            shortPath: shortenPath(path),
                            ...operation,
                        },
                    })),
            ),
        )
        .flat(2);

    // Sort the operations by tag ("Export" first, then alphabetically), path, and method:
    taggedOperations.sort(
        (
            { tag: tag1, operation: { method: method1, path: path1 } },
            { tag: tag2, operation: { method: method2, path: path2 } },
        ) =>
            Number(tag1 !== 'Export') - Number(tag2 !== 'Export') ||
            tag1.localeCompare(tag2) ||
            path1.localeCompare(path2) ||
            method1.localeCompare(method2),
    );

    // Group the operations by tag:
    const map = new Map<string, ApiOperationModel[]>();
    taggedOperations.forEach(({ tag, operation }) => {
        let operations = map.get(tag);
        if (!operations) {
            operations = [];
            map.set(tag, operations);
        }
        operations.push(operation);
    });

    return Array.from(map.entries()).map(([tag, operations]) => ({ tag, operations }));
}

function shortenPath(path: string): string {
    // Remove prefixes like "/tenant/{tenant}/", "/partner/{partner}/", and "/partner/{partner}/tenant/{tenantId}/":
    return path.replace(
        /^\/(?:partner\/\{partner\}\/tenant\/\{tenant(?:Id)?\}\/(?:partition\/\{partition\}\/)?|(?:(\w+)\/\{\1\}\/))/,
        '',
    );
}
